import {Injectable} from "@angular/core";
import { MapFacade } from '@core/ngrx/facades/map.facade';
import {DatacenterV2} from "./model/datacenter-v2";
import * as mapboxgl from "mapbox-gl";
import {DcMouseEventsHandler} from "./dc-mouse-events.handler";
import {MARKERS_LAYER_ID, MARKERS_SOURCE_ID} from "./consts/map.constants";
import {DataCentersService} from "./service/data-centers.service";
import {UserMap} from "./model/user-map";
import {Store} from "@ngrx/store";
import {
  datacenterActions, mapConfigActions, providersActions, polygonActions, mapFilesActions
} from 'src/app/core/ngrx/actions';
import {Provider} from "./model/providers";
import * as _ from 'lodash';
import {updatePolygonSum} from "./ngrx/actions/polygon.actions";
import {DatacentersSelector} from "./ngrx/selectors/datacenters.selector";
import {AvailabilityZoneHandler} from "./service/availability-zone.handler";
import {take, tap} from "rxjs";

@Injectable()
export class DatacentersHandler {
  constructor(private mouseEventsHandler: DcMouseEventsHandler, private datacentersService: DataCentersService,
              private readonly store: Store, private datacentersSelector: DatacentersSelector,
              private availabilityZoneHandler: AvailabilityZoneHandler, private mapFacade: MapFacade) {

  }

  public onUserMapSaved(map: mapboxgl.Map, userMap: UserMap, tempProviders: Provider[]) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, MARKERS_SOURCE_ID);
    let hasChanges = false;
    const azUpdates: DatacenterV2[] = []; // Store AZ updates to dispatch before nukeStore

    userMap.datacenters.forEach(datacenter => {
      if (datacenter.auxId) {
        const featureToUpdate = featureCollections.features.find(f => f.properties["auxId"] === datacenter.auxId);

        if (featureToUpdate) {
          let providerId = "";
          if (featureToUpdate.properties['providerId'].indexOf('AUX') != -1) {
            const provider = tempProviders.find(p => p.auxId === datacenter.providerId);
            providerId = provider?.id || featureToUpdate.properties['providerId'];
          } else {
            providerId = featureToUpdate.properties['providerId'];
          }

          featureToUpdate.properties = {
            ...datacenter,
            auxId: null,
            isVisible: true,
            providerId: providerId,
            hasChanged: false,
            toUpdate: false,
            toDelete: false,
            isDragging: false
          };

          delete featureToUpdate.properties['auxId'];
          hasChanges = true;

          if (datacenter.availabilityZoneId) {
            this.availabilityZoneHandler.updateAZIdAfterSave(datacenter.auxId, datacenter.id);
            // Store AZ update for later dispatch
            azUpdates.push({
              ...datacenter,
              auxId: datacenter.auxId // Keep auxId for the sidebar to identify the old entry
            });
          }
        }
      }
    });

    if (hasChanges) {
      source.setData(featureCollections);
    }

    // Dispatch AZ updates before nukeStore to ensure sidebar receives them
    azUpdates.forEach(datacenter => {
      this.store.dispatch(datacenterActions.datacenterModified({
        datacenter: datacenter
      }));
    });

    this.store.dispatch(
      datacenterActions.nukeStore()
    );
    this.store.dispatch(
      providersActions.loadProviders({mapId: userMap._id})
    );
  }

  public onProviderColorChanged(map: mapboxgl.Map, provider: Provider) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, MARKERS_SOURCE_ID);
    const providerId = provider.id || provider.auxId;
    const featuresToUpdate = featureCollections.features.filter(f => f.properties["providerId"] === providerId);
    for (const feature of featuresToUpdate) {
      feature.properties['providerColor'] = provider.color;
    }
    source.setData(featureCollections);
  }

  public onProviderVisibilityChanged(map: mapboxgl.Map, providers: Provider[], isVisible: boolean) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, MARKERS_SOURCE_ID);
    for (const provider of providers) {
      const featuresToUpdate = featureCollections.features.filter(f => f.properties["providerId"] === (provider.id || provider.auxId));
      for (const feature of featuresToUpdate) {
        feature.properties['isVisible'] = isVisible;
      }
    }

    source.setData(featureCollections);
  }

  public onThresholdChanged(map: mapboxgl.Map, mwThreshold: number) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, MARKERS_SOURCE_ID);
    for (const feature of featureCollections.features) {
      if ((feature.properties['mwLive'] || 0) < mwThreshold) {
        feature.properties['isVisible'] = false;
      } else {
        feature.properties['isVisible'] = true;
      }
    }
    source.setData(featureCollections);
  }

  public onDatacenterModified(map: mapboxgl.Map, datacenter: DatacenterV2) {
    const [source, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(map, MARKERS_SOURCE_ID);
    if (datacenter.auxId) {
      //new datacenter
      const featureToUpdate = featureCollections.features.find(f => f.properties["auxId"] === datacenter.auxId);
      if (datacenter.toUpdate && featureToUpdate) {
        featureToUpdate.geometry.coordinates = [datacenter.lon, datacenter.lat],
          featureToUpdate.properties = {
            ...datacenter
          }
      } else if (datacenter.toDelete) {
        featureCollections.features = featureCollections.features.filter(f => f.properties["auxId"] != datacenter.auxId);
      } else {
        const newFeatureCollection = this.createFeaturesFromDatacenters([datacenter]);
        featureCollections.features.push(...newFeatureCollection.features);
      }
      source.setData(featureCollections);
    } else {
      const featureToUpdate = featureCollections.features.find(f => f.properties["id"] === datacenter.id);
      if (featureToUpdate) {
        if (datacenter.toUpdate) {
          featureToUpdate.geometry.coordinates = [datacenter.lon, datacenter.lat],
            featureToUpdate.properties = {
              ...datacenter
            }
        } else if (datacenter.toDelete) {
          featureCollections.features = featureCollections.features.filter(f => f.properties["id"] != datacenter.id);
        }
      }
      source.setData(featureCollections);
    }
    //handle polygons sum
    Promise.resolve().then(() => {
      setTimeout(() => {
        this.store.dispatch(polygonActions.updatePolygonSum());
      }, 100);
    });
  }

  public onDatacentersReceive(map: mapboxgl.Map, datacenters: DatacenterV2[], lastBatch: boolean) {
    let geoJsonSource = map.getSource(MARKERS_SOURCE_ID) as any;
    let featureCollection: GeoJSON.FeatureCollection = null;
    if (!geoJsonSource) {
      featureCollection = this.createEmptyFeatureCollection();
      map.addSource(MARKERS_SOURCE_ID, {
        type: 'geojson',
        data: featureCollection,
      });
      this.addLayerForNoImageMarkers(map);
      this.addCenteredTextLayer(map);
      this.addGlowEffectLayer(map);
      this.addImageLayer(map);
      let customListenerWithMap = (event: any) => {
        this.mouseEventsHandler.onMarkerMouseDown(event, map);
      }
      let customClick = (event: any) => {
        this.mouseEventsHandler.onClick(event, map);
      }
      map.on('mousedown', MARKERS_LAYER_ID, customListenerWithMap);
      map.on('click', MARKERS_LAYER_ID, customClick);

      geoJsonSource = map.getSource(MARKERS_SOURCE_ID) as any;
    } else {
      if (!geoJsonSource || !geoJsonSource._data) {
        console.warn('DatacentersHandler: Source or data not available, creating empty collection');
        featureCollection = this.createEmptyFeatureCollection();
      } else {
        featureCollection = geoJsonSource._data;
      }
    }
    const newFeatureCollection = this.createFeaturesFromDatacenters(datacenters);
    featureCollection.features.push(...newFeatureCollection.features);
    geoJsonSource.setData(featureCollection);
    if (lastBatch) {
      this.datacentersSelector.getChangedDatacenters().pipe(
        take(1),
        tap((changedDatacenters) => {
          changedDatacenters.forEach((datacenter: DatacenterV2) => {
            this.onDatacenterModified(map, datacenter);
          });
        })
      ).subscribe();
    }
    this.mapFacade.saveOriginalMarkersGeoJson(structuredClone(featureCollection));
  }


  private createFeaturesFromDatacenters(datacenters: DatacenterV2[]): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: datacenters.map((dc) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [dc.lon, dc.lat],
        },
        properties: {
          ...dc,
          hasChanged: false,
          toUpdate: false,
          toDelete: false,
          isVisible: true,
          isDragging: false
        },
      })),
    };
  }

  private createEmptyFeatureCollection(): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  private addLayerForNoImageMarkers(map: mapboxgl.Map) {
    map.addLayer({
      id: MARKERS_LAYER_ID,
      type: 'circle',
      source: MARKERS_SOURCE_ID,
      filter: [
        'all',
        ['==', ['get', 'isVisible'], true],
      ],
      paint: {
        'circle-color': [
          'case',
          ['has', 'auxId'],
          '#FF0000',
          ['get', 'providerColor'],
        ],
        'circle-radius': [
          'interpolate',
          ['exponential', 1.5],
          ['zoom'],
          0, 5,
          6, 8,
          10, 15
        ],
        'circle-opacity': 0.9,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
      },
    });
  }

  private addCenteredTextLayer(map: mapboxgl.Map) {
    map.addLayer({
      id: MARKERS_LAYER_ID + '-text',
      type: 'symbol',
      source: MARKERS_SOURCE_ID,
      filter: [
        'all',
        ['==', ['get', 'isVisible'], true],
      ],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold'],
        'text-size': [
          'interpolate',
          ['exponential', 1.5],
          ['zoom'],
          0, 3,
          6, 7,
          10, 12
        ],
        'text-anchor': 'center',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 1,
      },
    });
  }

  private addImageLayer(map: mapboxgl.Map) {
    map.addLayer({
      id: MARKERS_LAYER_ID + '-image',
      type: 'symbol',
      source: MARKERS_SOURCE_ID,
      filter: [
        'all',
        ['==', ['get', 'isVisible'], true],
      ],
      layout: {
        'icon-image': ['get', 'providerId'],
        'icon-size': [
          'interpolate',
          ['exponential', 1.5],
          ['zoom'],
          0, 0.2,
          4, 0.2,
          6, 0.3,
          10, 0.6
        ],
        'icon-allow-overlap': true,
      },
    });
  }

  private addGlowEffectLayer(map: mapboxgl.Map) {
    // Add glow effect layer for new markers (DETRÁS de todo)
    map.addLayer({
      id: MARKERS_LAYER_ID + '-glow',
      type: 'circle',
      source: MARKERS_SOURCE_ID,
      filter: [
        'all',
        ['has', 'auxId'],
        ['==', ['get', 'isVisible'], true],
      ],
      paint: {
        'circle-color': '#00E676',
        'circle-radius': [
          'interpolate',
          ['exponential', 1.5],
          ['zoom'],
          0, 6,
          6, 9,
          10, 15
        ],
        'circle-opacity': 0.5
      },
    });
  }

  private startGlowAnimation(map: mapboxgl.Map): void {

    let glowPulse = 0;
    let increasing = true;

    const animateGlow = () => {
      if (increasing) {
        glowPulse += 0.008;
        if (glowPulse >= 0.7) {
          increasing = false;
        }
      } else {
        glowPulse -= 0.008;
        if (glowPulse <= 0.2) {
          increasing = true;
        }
      }

      try {
        map.setPaintProperty(MARKERS_LAYER_ID + '-glow', 'circle-opacity', glowPulse);
      } catch (error) {
        // Capa puede haber sido removida
      }

      requestAnimationFrame(animateGlow);
    };

    // Iniciar la animación
    requestAnimationFrame(animateGlow);
  }

}
